import React from 'react';
import useMetaTags from "../hooks/useMetaTags";
import {
  ShieldCheck,
  Database,
  Cookie,
  Share2,
  Clock,
  Lock,
  UserCheck,
  Baby,
  Link2,
  RefreshCw,
  Scale,
  Mail,
  MessageCircle,
} from 'lucide-react';

const LAST_UPDATED = "July 18, 2026";

const Section = ({ icon: Icon, title, children }) => (
  <div className="mb-10">
    <h2 className="flex items-center gap-3 text-2xl font-bold text-gray-800 mb-4">
      <span className="p-2 bg-[#679729]/10 rounded-lg text-[#679729]">
        <Icon size={20} />
      </span>
      {title}
    </h2>
    <div className="text-gray-700 leading-relaxed space-y-3 pl-1">
      {children}
    </div>
  </div>
);

const PrivacyPolicy = () => {
  useMetaTags({
    title: "Privacy Policy | Organic Pads",
    description: "Learn how OrganicPads collects, uses, shares, and protects your personal information, including data shared when you contact us via WhatsApp Business.",
    keywords: "organic pads privacy policy, data protection, whatsapp business data, DPDP act india, organicpads privacy",
    author: "Organic Pads",
    robots: "index, follow",
    canonical: "/privacy-policy",
    ogTitle: "Privacy Policy | Organic Pads",
    ogDescription: "How OrganicPads collects, uses, and safeguards your personal data.",
    ogImage: "https://organicpads.shop/logo.png",
    ogUrl: "https://organicpads.shop/privacy-policy",
    ogType: "website",
    twitterCard: "summary_large_image",
    twitterTitle: "Privacy Policy | Organic Pads",
    twitterDescription: "How OrganicPads collects, uses, and safeguards your personal data.",
    twitterImage: "https://organicpads.shop/logo.png",
    twitterSite: "@OrganicPads",
    twitterCreator: "@OrganicPads",
  });

  const schemaMarkup = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Privacy Policy",
    "url": "https://organicpads.shop/privacy-policy",
    "description": "OrganicPads' Privacy Policy detailing data collection, use, WhatsApp Business data handling, and user rights.",
    "publisher": { "@type": "Organization", "name": "OrganicPads", "url": "https://organicpads.shop" },
    "dateModified": "2026-07-18",
  };

  return (
    <>
      <div className="max-w-4xl mx-auto px-4 mt-10 py-16">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 text-[#679729] mb-4">
            <ShieldCheck size={24} />
            <span className="text-lg font-medium">Your Privacy Matters</span>
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-gray-800 mb-4">Privacy Policy</h1>
          <p className="text-gray-500">Last updated: {LAST_UPDATED}</p>
        </div>

        <p className="text-gray-700 leading-relaxed mb-10">
          This Privacy Policy explains how OrganicPads ("we", "us", "our"), the business that owns and operates the
          Organic Pads brand and the website organicpads.shop, collects, uses, shares, and protects your personal
          information when you visit our website, place an order, or contact us — including through WhatsApp
          Business. OrganicPads is a Micro, Small and Medium Enterprise (MSME)-registered business based in
          Ahmedabad, Gujarat, India. By using our website or services, you agree to the practices described below.
        </p>

        <Section icon={Database} title="1. Information We Collect">
          <p>We collect the following categories of information:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Contact details you provide, such as your name, email address, phone number, and shipping/billing address, when you place an order, sign up for updates, or contact us.</li>
            <li>Order details, including the products purchased, order value, and order history.</li>
            <li>Payment information — your payment is processed directly by our payment gateway partner; we do not store your card, UPI, or net-banking credentials on our own servers.</li>
            <li>Device and usage information, such as browser type, IP address, pages visited, and referring pages, collected automatically through cookies and similar technologies.</li>
            <li>Any information you voluntarily share with us through our contact form, email, or WhatsApp Business chat.</li>
          </ul>
        </Section>

        <Section icon={UserCheck} title="2. How We Use Your Information">
          <p>We use the information we collect to:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Process, ship, and fulfil your orders, and communicate order and delivery updates.</li>
            <li>Respond to your questions and provide customer support, including over WhatsApp Business.</li>
            <li>Send you marketing or promotional communications, where you have opted in — you can unsubscribe or opt out at any time using the link in our emails or by contacting us directly.</li>
            <li>Detect and prevent fraud, abuse, and unauthorized transactions.</li>
            <li>Understand how visitors use our website so we can improve our products, content, and user experience.</li>
          </ul>
        </Section>

        <Section icon={MessageCircle} title="3. WhatsApp Business & Meta Platform Data">
          <p>
            We use WhatsApp Business to communicate with customers who choose to message us — for example, for order
            support, product questions, or delivery updates. When you message OrganicPads on WhatsApp, your phone
            number, profile information, and the content of your messages are processed by WhatsApp/Meta Platforms,
            Inc. in accordance with Meta's own privacy and data policies, in addition to this Privacy Policy.
          </p>
          <p>
            We use the information exchanged over WhatsApp Business solely to respond to and support you as a
            customer. We do not use WhatsApp conversation data for advertising, and we do not sell or share this
            information with third parties for their own marketing purposes.
          </p>
        </Section>

        <Section icon={Cookie} title="4. Cookies & Tracking Technologies">
          <p>
            Our website uses cookies and similar technologies to keep the site working correctly, remember your
            preferences, and understand how visitors use our site through basic analytics. You can control or
            disable cookies through your browser settings; doing so may affect some features of the website.
          </p>
        </Section>

        <Section icon={Share2} title="5. Information Sharing & Disclosure">
          <p>We share personal information only with:</p>
          <ul className="list-disc pl-6 space-y-1">
            <li>Payment gateway providers, to securely process your payment.</li>
            <li>Courier and logistics partners, to deliver your order to you.</li>
            <li>Email/SMS/WhatsApp service providers, to send order updates and, where you've opted in, marketing communications.</li>
            <li>Government or regulatory authorities, where required by applicable law.</li>
          </ul>
          <p className="font-medium">We do not sell your personal information to anyone.</p>
        </Section>

        <Section icon={Clock} title="6. Data Retention">
          <p>
            We retain your order and account information for as long as needed to fulfil the purpose it was
            collected for — including order fulfilment, customer support, warranty/refund handling, and any
            accounting or legal record-keeping obligations under Indian law.
          </p>
        </Section>

        <Section icon={Lock} title="7. Data Security">
          <p>
            We take reasonable technical and organizational measures to protect your personal information against
            unauthorized access, alteration, disclosure, or destruction. However, no method of transmission or
            storage over the internet is completely secure, and we cannot guarantee absolute security.
          </p>
        </Section>

        <Section icon={ShieldCheck} title="8. Your Rights">
          <p>
            You may request access to, correction of, or deletion of your personal information, or opt out of
            marketing communications, by contacting us using the details below. If you are located in India, these
            rights are provided in line with the Digital Personal Data Protection Act, 2023. If you are visiting
            from the European Union or European Economic Area, you may also have rights under the General Data
            Protection Regulation (GDPR) with respect to data we process about you.
          </p>
        </Section>

        <Section icon={Baby} title="9. Children's Privacy">
          <p>
            Our website and services are not directed at, and we do not knowingly collect personal information
            from, individuals under the age of 18. If you believe a minor has provided us with personal information,
            please contact us so we can remove it.
          </p>
        </Section>

        <Section icon={Link2} title="10. Third-Party Links">
          <p>
            Our website may contain links to third-party websites or services, including social media platforms.
            We are not responsible for the privacy practices or content of those external sites, and we encourage
            you to review their respective privacy policies.
          </p>
        </Section>

        <Section icon={RefreshCw} title="11. Changes to This Policy">
          <p>
            We may update this Privacy Policy from time to time to reflect changes in our practices or for legal,
            operational, or regulatory reasons. Any changes will be posted on this page with a revised "Last
            updated" date.
          </p>
        </Section>

        <Section icon={Scale} title="12. Governing Law">
          <p>
            This Privacy Policy is governed by the laws of India, and any disputes arising from it will be subject
            to the exclusive jurisdiction of the courts in Ahmedabad, Gujarat.
          </p>
        </Section>

        <Section icon={Mail} title="13. Contact Us">
          <p>If you have questions about this Privacy Policy or how we handle your data, reach out to us:</p>
          <ul className="list-none space-y-1">
            <li>Email: <a href="mailto:organicpads.shop@gmail.com" className="text-[#679729] hover:underline">organicpads.shop@gmail.com</a></li>
            <li>Phone: <a href="tel:+917203802493" className="text-[#679729] hover:underline">+91 720 3802 493</a></li>
            <li>Address: Nikol, Ahmedabad, Gujarat 382350</li>
          </ul>
        </Section>
      </div>
      <script type="application/ld+json">
        {JSON.stringify(schemaMarkup)}
      </script>
    </>
  );
};

export default PrivacyPolicy;
